import React, {useImperativeHandle, useMemo, useCallback, forwardRef, FC, ReactNode} from 'react';
import {Props} from './ac-off-canvas.proptypes';
import clsx from 'clsx';

// Imports => Constants
import {ICONS, THEMES} from '@asdf-constants';

// Imports => Utilities
import {AcIsFunction} from '@asdf-utils';

// Imports => Atoms
import {AcIcon, AcRipple} from '@asdf-atoms';

const _CLASSES = {
  MAIN: 'ac-off-canvas',
  VISIBLE: 'ac-off-canvas--visible',
  DENSE: 'ac-off-canvas--dense',
  RAISED: 'ac-off-canvas--raised',
  WIDE: 'ac-off-canvas--wide',
  TOGGLE: 'ac-off-canvas__toggle',
  POSITION: {
    LEFT: 'ac-off-canvas--position-left',
    RIGHT: 'ac-off-canvas--position-right',
  },
  THEME: {
    DEFAULT: 'ac-off-canvas--default',
    TRANSPARENT: 'ac-off-canvas--transparent',
    WHITE: 'ac-off-canvas--white',
    LIGHT: 'ac-off-canvas--light',
    SUBTLE: 'ac-off-canvas--subtle',
    MEDIUM: 'ac-off-canvas--medium',
    PITCH: 'ac-off-canvas--pitch',
    ALPHA: 'ac-off-canvas--alpha',
    ALPHA_LIGHT: 'ac-off-canvas--alpha-light',
    OMEGA: 'ac-off-canvas--omega',
    OMEGA_LIGHT: 'ac-off-canvas--omega-light',
  },
};

type AcOffCanvasProps = {
  children: ReactNode,
  position?: string,
  theme?: string,
  visible?: boolean,
  dense?: boolean,
  withToggle?: boolean,
  raised?: boolean,
  wide?: boolean,
  callback?: any,
  className?: string,
}
const AcOffCanvas:FC<AcOffCanvasProps> = forwardRef(
  (
    {
      children,
      position = 'left',
      theme = 'default',
      visible = true,
      dense = false,
      withToggle = true,
      raised = false,
      wide = false,
      callback = () => {},
      className,
      ...rest
    },
    ref
  ) => {
    const handleToggle = useCallback(async event => {
      if (event && event.preventDefault) event.preventDefault();
      if (event && event.stopPropagation) event.stopPropagation();

      if (callback && AcIsFunction(callback)) callback();
    }, []);

    const getMainClassNames = useMemo(() => {
      return clsx(
        _CLASSES.MAIN,
        position && _CLASSES.POSITION[position.toUpperCase()],
        theme && _CLASSES.THEME[theme.toUpperCase()],
        visible && _CLASSES.VISIBLE,
        dense && _CLASSES.DENSE,
        raised && _CLASSES.RAISED,
        wide && _CLASSES.WIDE,
        className
      );
    }, [position, theme, visible, dense, raised, wide, className]);

    const getToggleClassNames = useMemo(() => {
      return clsx(_CLASSES.TOGGLE);
    });

    useImperativeHandle(ref, () => ({
      toggle: handleToggle,
    }));

    return (
      <aside aria-modal={true} role={'dialog'} className={getMainClassNames} {...rest}>
        {children}

        {withToggle && (
          <div
            className={getToggleClassNames}
            onClick={handleToggle}
            title={visible ? 'Collapse (shortkey: ] )' : 'Expand (shortkey: [ )'}
          >
            <AcRipple theme={THEMES.WHITE} simple />
            <AcIcon
              icon={ICONS.CHEVRON_LEFT}
              title={visible ? 'Collapse (shortkey: ] )' : 'Expand (shortkey: [ )'}
            />
          </div>
        )}
      </aside>
    );
  }
);

AcOffCanvas.displayName = 'AcOffCanvas';
AcOffCanvas.propTypes = Props;

export {AcOffCanvas};
export default AcOffCanvas;
